<template>
  <div class="locations-section__app" v-if="!loading">

    <div class="locations-section__app_header">
      <h3>{{ translations?.filters }}</h3>

      <!-- Filters toggle button for mobile. -->
      <button class="d-lg-none mb-2 caret-button" @click="isNavCollapsed = !isNavCollapsed"
        :class="{ 'rotated': !isNavCollapsed }" :aria-expanded="!isNavCollapsed" :aria-controls="'filter-nav'"
        :aria-label="isNavCollapsed ? `${translations?.filters}: Show filters` : `${translations?.filters}: Hide filters`">
      </button>
    </div>

    <nav class="locations-section__app_filters">
      <ul class="custom_nav category-nav" :class="{ 'collapse': isNavCollapsed }">

        <li>
          <div class="input field-dropdown">
            <div class="select-wrapper">
              <label for="select_category">{{ translations?.locationType }}</label>
              <select ref="categorySelect" id="select_category" v-model="selectedCategory">
                <option value="all">{{ translations?.allLocations }}</option>
                <option :value="category.slug" v-for="category in locationCategories" :key="category.slug">
                  {{ category.title }}
                </option>
              </select>
            </div>
          </div>
        </li>

        <li>
          <div class="input field-dropdown">
            <div class="select-wrapper">
              <label for="select_services">{{ translations?.services }}</label>
              <select ref="serviceSelect" id="select_services" v-model="selectedService"></select>
            </div>
          </div>
        </li>

        <li v-if="selectedCategory !== 'all' || selectedService !== 'all'" class="clear-btn">
          <div class="input field-dropdown">
            <button class="btn btn-secondary" @click="clearFilters">
              Clear
            </button>
          </div>
        </li>

      </ul>
    </nav>

    <location-map v-if="Object.keys(filteredLocations).length > 0" class="locations-map"
      :selected-category="selectedCategory" :selected-service="selectedService" :options="options"></location-map>

    <div class="locations-section__app_locations" v-if="Object.keys(filteredLocations).length > 0">
      <div class="locations-section__app_locations_category" :id="categorySlug" v-for="(category, categorySlug) in filteredLocations" :key="categorySlug">
        <div class="locations-section__app_locations_category_heading">
          <a :href="'/locations#'+ categorySlug">
            <h3>{{ category.categoryTitle }}</h3>
          </a>
          <p v-if="category.categoryText">{{ category.categoryText }}</p>
        </div>
        <div class="locations-section__app_locations_elts" :class="categorySlug">
          <div class="locations-section__app_locations_col" v-for="location in category.locations" :key="location.slug">
            <div :class="[
              'locations-section__app_locations_elt',
              'card',
              { 'card-location': categorySlug === 'healthcare-centers', 'card-location-link': categorySlug === 'healthcare-centers' }
            ]">
              <figure v-if="categorySlug === 'healthcare-centers'">
                <img :src="location.locationImageUrl" :alt="location.locationImageAlt" role="img"
                  aria-label="{{ location.locationImageAlt }}" v-if="location.locationImageUrl"
                  class="img-fluid location-img">
                <img v-else-if="!location.locationImageUrl && location.locationCategoryImageUrl"
                  :src="location.locationCategoryImageUrl" :alt="location.locationCategoryImageAlt" role="img"
                  aria-label="{{ location.locationCategoryImageAlt }}" class="img-fluid location-img">
              </figure>

              <GMapMap v-if="categorySlug === 'healthcare-centers'" :id="'map-' + location.slug" class="location-map"
                :center="{ lat: parseFloat(location.lat), lng: parseFloat(location.lng) }" :zoom="15"
                @idle="onMapReady"
                style="width: 100%; height: 200px" :options="{ ...options, draggable: false, clickable: false }"
                :key="location.slug" ref="gmap">
                <GMapMarker :position="{ lat: parseFloat(location.lat), lng: parseFloat(location.lng) }"
                  :icon="'/images/active-health-center.svg'" :title="location.slug"></GMapMarker>
              </GMapMap>

              <div class="locations-section__app_locations_elt_info">
                <component :is="categorySlug === 'healthcare-centers' ? 'a' : 'div'"
                  v-bind="categorySlug === 'healthcare-centers' ? { class: 'card-location-link', href: location.url, role: 'button', tabindex: '0' } : {}">
                  <h4 class="locations-section__app_locations_elt_info_title">{{ location.title }}</h4>
                  <address v-if="categorySlug === 'healthcare-centers'"
                    class="locations-section__app_locations_elt_info_address">
                    {{ location.street }} <br />
                    {{ location.city }},
                    {{ location.state }}
                    {{ location.zip }}
                  </address>
                  <address v-else class="locations-section__app_locations_elt_info_address">
                    <template v-if="location.street">
                      {{ location.street }} <br />
                    </template>
                    <template v-if="location.city">
                      {{ location.city }},
                    </template>
                    <template v-if="location.state">
                      {{ location.state }}
                    </template>
                    <template v-if="location.zip">
                      <template></template> {{ location.zip }}
                    </template>
                  </address>
                </component>
                <a v-if="categorySlug !== 'healthcare-centers'"
                  class="locations-section__app_locations_elt_info_telephone" :href="'tel:' + location.phone">{{
                    location.phone }}</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="locations-section__app_nomatch" v-else>
      <p><span class="nomatch_icon"></span>No Results Found</p>
      <span>There are no results that match those filters.</span>
    </div>
  </div>
</template>

<script>
import locationMap from './location-map.vue'

export default {
  data() {
    return {
      selectedCategory: 'all',
      selectedService: 'all',
      isNavCollapsed: true,
      categoryChoices: null,
      serviceChoices: null,
      _didScrollToHash: false,
    }
  },
  components: {
    locationMap
  },

  props: ['options', 'locations', 'locationCategories', 'locationServices', 'loading', 'translations'],

  computed: {
    filteredLocations() {
      // Copy of all locations.
      let filtered = JSON.parse(JSON.stringify(this.locations)); // Create a deep copy to avoid mutating props

      // Apply category filter.
      if (this.selectedCategory !== 'all') {
        filtered = Object.fromEntries(
          Object.entries(filtered).filter(([categorySlug]) => categorySlug === this.selectedCategory)
        );
      }

      // Apply service filter.
      if (this.selectedService !== 'all') {
        Object.keys(filtered).forEach(categorySlug => {
          filtered[categorySlug].locations = filtered[categorySlug].locations.filter(location => {
            return location.relatedServices.some(service => service.slug === this.selectedService);
          });
        });

        // Remove any empty categories after filtering by service
        filtered = Object.fromEntries(
          Object.entries(filtered).filter(([_, category]) => category.locations.length > 0)
        );
      }

      return filtered;
    },
  },

  mounted() {
    this.initializeChoices();
    this.$nextTick(() => {
      this.addTitlesToIframes();
    });
   window.addEventListener('hashchange', this.resetHashScrollFlag);
  },

  updated() {
    this.initializeChoices();
    this.$nextTick(() => {
      this.addTitlesToIframes();
    });
  },

  beforeUnmount() {
    this.destroyChoices();
   window.removeEventListener('hashchange', this.resetHashScrollFlag);
  },

  methods: {
     resetHashScrollFlag() {
    this._didScrollToHash = false;
  },

  onMapReady() {
    if (this._didScrollToHash) return;

    const hash = window.location.hash;
    if (!hash) return;

    const id = decodeURIComponent(hash.slice(1));
    const el = document.getElementById(id);
    if (!el) return;

    this._didScrollToHash = true;
    el.scrollIntoView({ behavior: 'auto', block: 'start' });
  },

    initializeChoices() {
      if (this.categoryChoices) {
        this.categoryChoices.destroy();
      }
      if (this.serviceChoices) {
        this.serviceChoices.destroy();
      }

      const categorySelect = this.$refs.categorySelect;
      const serviceSelect = this.$refs.serviceSelect;

      if (categorySelect) {
        this.categoryChoices = new Choices(categorySelect, {
          searchEnabled: false,
          itemSelectText: '',
          shouldSort: false,
        });
      }
      if (serviceSelect) {
        const choicesData = [
          { value: 'all', label: this.translations?.allServices }
        ].concat(
          this.locationServices.map(service => ({
            value: service.slug,
            label: service.icon
              ? `<img src="${service.icon}" alt="${service.title}" class="choice-icon" />${service.title}`
              : service.title,
            id: service.slug
          }))
        );

        this.serviceChoices = new Choices(serviceSelect, {
          allowHTML: true,
          choices: choicesData,
          searchEnabled: false,
          itemSelectText: '',
          shouldSort: false,
        });

        // Set initial value in Choices.js.
        this.serviceChoices.setChoiceByValue(this.selectedService);

        // Update Vue's reactivity manually.
        serviceSelect.addEventListener('change', (event) => {
          this.selectedService = event.target.value;

          this.$nextTick(() => {
            this.$forceUpdate();
          });
        });
      }
    },

    destroyChoices() {
      if (this.categoryChoices) {
        this.categoryChoices.destroy();
      }
      if (this.serviceChoices) {
        this.serviceChoices.destroy();
      }
    },

    clearFilters() {
      // Reset selected filters.
      this.selectedCategory = 'all';
      this.selectedService = 'all';
    },

    addTitlesToIframes() {
      setTimeout(() => {
        try {
          const locationElements = document.querySelectorAll('.locations-section__app_locations_elt');

          // Bail early if no location elements found.
          if (!locationElements || locationElements.length === 0) {
            return;
          }

          locationElements.forEach((locationElement, index) => {
            // Skip if location element is missing.
            if (!locationElement) {
              return;
            }

            const iframe = locationElement.querySelector('iframe');
            const titleElement = locationElement.querySelector('.locations-section__app_locations_elt_info_title');

            // Skip if either iframe or title element is missing
            if (!iframe || !titleElement) {
              return;
            }

            // Only update if title is missing or empty.
            if (!iframe.hasAttribute('title') || iframe.getAttribute('title').trim() === '') {
              const locationName = titleElement.textContent?.trim() || `Location ${index + 1}`;
              const dynamicTitle = `Google Maps location for ${locationName}`;

              try {
                iframe.setAttribute('title', dynamicTitle);
              } catch (attributeError) {
                console.error('Error setting iframe title:', attributeError);
              }
            }
          });
        } catch (error) {
          console.error('Error in addTitlesToIframes:', error);
        }
      }, 750);
    }
  },
}
</script>
